<template>
  <div class="flex h-screen bg-gray-50">
    <SideBar class="flex-shrink-0" />

    <div class="flex-1 overflow-auto">
      <div class="p-6">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
          <div>
            <h1 class="text-2xl font-bold">Call Logs</h1>
            <p class="text-gray-500">Review & replay your calls</p>
          </div>

          <!-- Filters -->
          <div class="flex flex-wrap items-center gap-3">
            <!-- User filter (only for user id 1) -->
            <select
              v-if="isAdminUser && userOptions.length"
              v-model="selectedUserId"
              class="border rounded-lg text-sm py-2 px-3 w-40 bg-white"
            >
              <option value="">All users</option>
              <option
                v-for="user in userOptions"
                :key="user.id"
                :value="user.id"
              >
                {{ user.name }}
              </option>
            </select>

            <!-- Date from -->
            <input
              v-model="dateFrom"
              type="date"
              class="border rounded-lg text-sm py-2 px-3 bg-white"
            />

            <!-- Date to -->
            <input
              v-model="dateTo"
              type="date"
              class="border rounded-lg text-sm py-2 px-3 bg-white"
            />

            <!-- Search -->
            <div class="relative">
              <span
                class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              >🔍</span>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search by number…"
                class="pl-10 pr-3 py-2 border rounded-lg text-sm w-48"
              />
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="overflow-hidden rounded-lg border bg-white">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50 sticky top-0">
              <tr>
                <th class="px-4 py-2 text-xs text-gray-500 uppercase text-left">
                  Caller
                </th>
                <th
                  @click="sort('from_number')"
                  class="px-4 py-2 text-xs text-gray-500 uppercase cursor-pointer"
                >
                  From
                  <span v-if="sortBy === 'from_number'">
                    {{ sortDir === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
                <th
                  @click="sort('to_number')"
                  class="px-4 py-2 text-xs text-gray-500 uppercase cursor-pointer"
                >
                  To
                  <span v-if="sortBy === 'to_number'">
                    {{ sortDir === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
                <th
                  @click="sort('status')"
                  class="px-4 py-2 text-xs text-gray-500 uppercase cursor-pointer"
                >
                  Status
                  <span v-if="sortBy === 'status'">
                    {{ sortDir === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
                <th
                  @click="sort('start_time')"
                  class="px-4 py-2 text-xs text-gray-500 uppercase cursor-pointer"
                >
                  Start
                  <span v-if="sortBy === 'start_time'">
                    {{ sortDir === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
                <th
                  @click="sort('total_time')"
                  class="px-4 py-2 text-xs text-gray-500 uppercase cursor-pointer"
                >
                  Duration
                  <span v-if="sortBy === 'total_time'">
                    {{ sortDir === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
                <th class="px-4 py-2 text-xs text-gray-500 uppercase text-left">
                  Recording
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr
                v-for="call in calls"
                :key="call.id"
                class="hover:bg-gray-50"
              >
                <!-- Caller avatar/initials -->
                <td class="px-4 py-2">
                  <div
                    class="inline-flex items-center justify-center w-8 h-8 rounded-full text-black text-sm font-bold"
                    :style="{ backgroundColor: getAvatarColor(call.user_id) }"
                    :title="usernameById(call.user_id)"
                  >
                    {{ getInitials(usernameById(call.user_id)) }}
                  </div>
                </td>
                <td class="px-4 py-2">{{ call.from_number }}</td>
                <td class="px-4 py-2">{{ call.to_number }}</td>
                <td class="px-4 py-2">{{ call.status }}</td>
                <td class="px-4 py-2">{{ formatDate(call.start_time) }}</td>
                <td class="px-4 py-2">{{ call.total_time }}</td>
                <td class="px-4 py-2">
                  <!-- Play button until loaded -->
                  <button
                    v-if="call.recording_id && !loaded[call.recording_id]"
                    @click="loadAndPlay(call.recording_id)"
                    class="px-3 py-1 bg-primary text-black rounded-lg hover:bg-primary/90 text-sm"
                  >
                    ▶ Play
                  </button>

                  <!-- Audio scrubber once loaded -->
                  <audio
                    v-else-if="loaded[call.recording_id]"
                    :src="loaded[call.recording_id]"
                    controls
                    autoplay
                    class="h-8"
                  ></audio>
                  <span v-else class="text-gray-400">—</span>

                  <button
                    @click="processFeedback(call)"
                    class="px-3 mx-3 py-1 ai-button"
                  >
                    {{ feedbackButtonText(call) }}
                  </button>
                </td>
              </tr>
              <tr v-if="calls.length === 0">
                <td
                  colspan="7"
                  class="py-6 text-center text-gray-500"
                >
                  No calls found
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="mt-4 flex justify-between items-center">
          <div class="text-sm text-gray-700">
            Showing {{ startIndex + 1 }}–{{ endIndex }} of {{ total }} calls
          </div>
          <div class="flex gap-2">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="px-3 py-1 border rounded disabled:opacity-50"
            >
              Prev
            </button>
            <button
              @click="nextPage"
              :disabled="currentPage === totalPages"
              class="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal -->
  <div
    v-if="callFeedBack"
    class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
  >
    <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-2xl p-6 relative">
      <h2 class="text-xl font-bold mb-2">AI Call Feedback</h2>
      <div
        class="text-sm text-gray-800 max-h-[70vh] overflow-auto prose"
        v-html="convertToHtml(callFeedBack)"
      ></div>

      <button
        @click="callFeedBack = false"
        class="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl"
      >
        ×
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import debounce from 'lodash/debounce'
import SideBar from '@/components/SideBar.vue'
import { fetchRecording } from '@/api/leads'
import { marked } from 'marked'

marked.setOptions({
  breaks: false
})

const store = useStore()
const calls = ref([])
const total = ref(0)
const loaded = reactive({}) // recording_id -> proxy URL

const searchQuery = ref('')
const sortBy = ref('start_time')
const sortDir = ref('desc')
const currentPage = ref(1)
const itemsPerPage = ref(10)

// filters
const selectedUserId = ref('')
const dateFrom = ref('')
const dateTo = ref('')

const callFeedBack = ref(false)

// current user (for showing dropdown only to user id 1)
const currentUserId = computed(() => {
  return store.state.auth?.user?.id ?? null
})

const isAdminUser = computed(() => currentUserId.value === 1)

onMounted(async () => {
  await store.dispatch('leads/fetchUserMap')
  loadCalls()
})

const totalPages = computed(() =>
  Math.ceil(total.value / itemsPerPage.value)
)
const startIndex = computed(
  () => (currentPage.value - 1) * itemsPerPage.value
)
const endIndex = computed(() =>
  Math.min(startIndex.value + itemsPerPage.value, total.value)
)

// helper getters
const usernameById = id =>
  store.getters['leads/usernameById'](id)

const getInitials = name =>
  name
    .split(' ')
    .map(p => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

const getAvatarColor = id =>
  [
    '#FFB3BA',
    '#FFDFBA',
    '#FFFFBA',
    '#BAFFC9',
    '#BAE1FF',
    '#E5BAFF',
    '#D5F4E6',
    '#FFCCE5',
    '#C5FAD5',
    '#FFF2CC'
  ][id % 10]

const formatDate = d => new Date(d).toLocaleString()

// usersFiltered from store (same logic as analytics dashboard)
const usersFiltered = computed(
  () => store.getters['leads/usersFiltered'] || {}
)

const userOptions = computed(() =>
  Object.entries(usersFiltered.value).map(([id, name]) => ({
    id,
    name
  }))
)

// fetch paginated calls
async function loadCalls() {
  const payload = {
    search: searchQuery.value,
    sortBy: sortBy.value,
    sortDir: sortDir.value,
    page: currentPage.value,
    limit: itemsPerPage.value,
    user_id: selectedUserId.value || undefined,
    date_from: dateFrom.value || undefined,
    date_to: dateTo.value || undefined
  }

  const { data, meta } = await store.dispatch(
    'leads/fetchCalls',
    payload
  )
  calls.value = data
  total.value = meta.total
}

// debounce on search box
const debouncedLoad = debounce(() => {
  currentPage.value = 1
  loadCalls()
}, 300)

function sort(col) {
  if (sortBy.value === col) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortBy.value = col
    sortDir.value = 'asc'
  }
  currentPage.value = 1
  loadCalls()
}

function prevPage() {
  if (currentPage.value > 1) {
    currentPage.value--
    loadCalls()
  }
}

function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
    loadCalls()
  }
}

function feedbackButtonText(call) {
  if (
    (call.ai_feedback_status === 'processing' ||
      call.ai_feedback_status === 'requested') &&
    !call.ai_feedback_summary
  ) {
    return 'Processing...'
  }
  if (call.ai_feedback_summary) {
    return 'View Feedback'
  }
  return 'Request Feedback'
}

async function processFeedback(call) {
  if (
    (call.ai_feedback_status === 'processing' ||
      call.ai_feedback_status === 'requested') &&
    !call.ai_feedback_summary
  ) {
    return
  } else if (call.ai_feedback_summary) {
    callFeedBack.value = call.ai_feedback_summary
  } else {
    await store.dispatch(
      'calls/markCallAsProcessing',
      call.id
    )
    store.dispatch('leads/fetchUserMap')
    loadCalls()
  }
}

function convertToHtml(md) {
  return marked(md).replace(/\n/g, '<br>')
}

// search text
watch(searchQuery, debouncedLoad)

// sort + page
watch([sortBy, sortDir, currentPage], () => {
  loadCalls()
})

// filters (user + date range)
watch([selectedUserId, dateFrom, dateTo], () => {
  currentPage.value = 1
  loadCalls()
})

/**
 * on demand play
 */
async function loadAndPlay(recordingId) {
  if (!recordingId || loaded[recordingId]) return
  try {
    loaded[recordingId] = await fetchRecording(recordingId)
  } catch (err) {
    console.error('Failed to load recording:', err)
  }
}
</script>

<style>
.ai-button {
  background-color: #fff;
  border-radius: 10px;
  font-size: 14px;
  color: #3f52ff;
  border: 1px solid #3f52ff;
}
</style>
